/**
 * Helper pour déterminer le contexte pricing et les textes à afficher
 * sur les pages PaymentSetup et PaymentBlocked.
 *
 * 6 scénarios couverts :
 * 1. new_trial      — Nouveau user, premier passage après onboarding
 * 2. trial_expired   — Essai expiré, jamais payé
 * 3. payment_failed  — Paiement échoué (past_due)
 * 4. canceled_paid   — Abonnement annulé volontairement (avait payé)
 * 5. unpaid          — Impayé après plusieurs relances
 * 6. incomplete      — Checkout Stripe abandonné
 */

export type PricingContext =
  | 'new_trial'
  | 'trial_expired'
  | 'payment_failed'
  | 'canceled_paid'
  | 'unpaid'
  | 'incomplete';

export interface PricingTexts {
  badge?: string;
  title: string;
  subtitle: string;
  subtitleHighlight?: string;
  buttonLabel: string;
  footer: string;
  reassurance?: string;
  secondaryButtonLabel?: string;
  bullets?: string[];
}

interface PricingContextInput {
  status: string;
  hasStripeSubscription: boolean;
  hasHadTrial: boolean;
  isInTrial: boolean;
}

/**
 * Détermine le scénario pricing à partir du state subscription
 */
export function getPricingContext(input: PricingContextInput): PricingContext {
  const { status, hasStripeSubscription, hasHadTrial, isInTrial } = input;

  if (status === 'past_due') return 'payment_failed';
  if (status === 'unpaid') return 'unpaid';
  if (status === 'incomplete') return 'incomplete';
  if (status === 'canceled' && hasStripeSubscription) return 'canceled_paid';
  if (hasHadTrial && !isInTrial) return 'trial_expired';

  return 'new_trial';
}

/**
 * Retourne les textes à afficher selon le contexte pricing
 */
export function getPricingTexts(context: PricingContext): PricingTexts {
  switch (context) {
    case 'new_trial':
      return {
        badge: '14 jours d\'essai gratuit',
        title: 'Dernière étape avant de commencer',
        subtitle: 'Choisissez votre plan pour démarrer votre essai gratuit de 14 jours.',
        subtitleHighlight: 'Aucun prélèvement pendant l\'essai.',
        buttonLabel: 'Commencer l\'essai gratuit',
        footer: 'Paiement sécurisé par Stripe. Annulez à tout moment pendant l\'essai.',
      };

    case 'trial_expired':
      return {
        badge: undefined,
        title: 'Votre essai gratuit est terminé',
        subtitle: 'Choisissez un plan pour continuer à utiliser B8ild. Vos données sont conservées.',
        buttonLabel: 'S\'abonner',
        footer: 'Paiement sécurisé par Stripe. Annulez à tout moment.',
        reassurance: '✓ Vos chantiers, factures et données sont toujours là',
      };

    case 'payment_failed':
      return {
        title: 'Problème de paiement',
        subtitle: 'Votre dernier prélèvement a échoué. Mettez à jour votre moyen de paiement pour retrouver l\'accès.',
        buttonLabel: 'Mettre à jour ma carte bancaire',
        secondaryButtonLabel: 'Changer de plan',
        footer: '',
        reassurance: '✓ Vos données sont intactes et seront accessibles dès la régularisation',
        bullets: [
          'Nos tentatives de prélèvement ont échoué',
          'Vérifiez que votre carte bancaire est valide et approvisionnée',
          'Mettez à jour votre moyen de paiement via le bouton ci-dessous',
        ],
      };

    case 'canceled_paid':
      return {
        title: 'Votre abonnement est terminé',
        subtitle: 'Vous avez annulé votre abonnement. Réabonnez-vous pour retrouver l\'accès à B8ild.',
        buttonLabel: 'Se réabonner',
        secondaryButtonLabel: 'Vérifier mon statut',
        footer: '',
        reassurance: '✓ Vos données sont conservées',
        bullets: [
          'Votre abonnement a pris fin',
          'Vos données sont conservées',
          'Réabonnez-vous pour retrouver l\'accès',
        ],
      };

    case 'unpaid':
      return {
        title: 'Accès suspendu',
        subtitle: 'Votre abonnement est impayé depuis plusieurs tentatives. Régularisez votre situation pour retrouver l\'accès.',
        buttonLabel: 'Régulariser mon paiement',
        secondaryButtonLabel: 'Nous contacter',
        footer: '',
        reassurance: '✓ Vos données sont intactes et seront accessibles dès la régularisation',
        bullets: [
          'Plusieurs tentatives de prélèvement ont échoué',
          'Votre accès est suspendu',
          'Régularisez via le bouton ci-dessous',
        ],
      };

    case 'incomplete':
      return {
        badge: undefined,
        title: 'Finalisez votre inscription',
        subtitle: 'Vous avez commencé la configuration de votre paiement. Choisissez un plan pour terminer.',
        buttonLabel: 'Finaliser mon abonnement',
        footer: 'Paiement sécurisé par Stripe.',
      };
  }
}
